import { BadgeProps } from '@commenda/kakashi';
import { SalesTaxFilingFrequencyEnum } from '@commenda/prisma';
import { FilingReviewStatus } from '../../../molecules/filings/types';
import { FilingFrequencyConfig, FilingStatusConfig } from './types';

// ============================================================================
// Filing Status Configuration
// ============================================================================

export const FILING_STATUS_CONFIG: Record<
  FilingReviewStatus,
  FilingStatusConfig
> = {
  [FilingReviewStatus.ACTIVE]: {
    label: 'Active',
    badgeVariant: 'blue' as BadgeProps['variant'],
    bgColor: 'bg-utility-blue-50',
    borderColor: 'border-utility-blue-200',
    textColor: 'text-utility-blue-700',
    dotColor: 'bg-utility-blue-500',
  },
  [FilingReviewStatus.ACCRUING]: {
    label: 'Accruing',
    badgeVariant: 'warning' as BadgeProps['variant'],
    bgColor: 'bg-warning-primary',
    borderColor: 'border-utility-warning-200',
    textColor: 'text-utility-warning-700',
    dotColor: 'bg-utility-warning-500',
  },
  [FilingReviewStatus.FILED]: {
    label: 'Filed',
    badgeVariant: 'success' as BadgeProps['variant'],
    bgColor: 'bg-success-10',
    borderColor: 'border-utility-success-200',
    textColor: 'text-utility-success-700',
    dotColor: 'bg-utility-success-500',
  },
  [FilingReviewStatus.OVERDUE]: {
    label: 'Overdue',
    badgeVariant: 'error' as BadgeProps['variant'],
    bgColor: 'bg-error-10',
    borderColor: 'border-utility-error-200',
    textColor: 'text-utility-error-700',
    dotColor: 'bg-utility-error-500',
  },
};

// ============================================================================
// Filing Frequency Configuration
// ============================================================================

export const FILING_FREQUENCY_CONFIG: FilingFrequencyConfig[] = [
  {
    label: 'Monthly',
    value: SalesTaxFilingFrequencyEnum.MONTHLY,
    minMonths: 1,
  },
  {
    label: 'Quarterly',
    value: SalesTaxFilingFrequencyEnum.QUARTERLY,
    minMonths: 3,
  },
  {
    label: 'Quarterly Prepay Monthly',
    value: SalesTaxFilingFrequencyEnum.QUARTERLY_PREPAY_MONTHLY,
    minMonths: 3,
  },
  {
    label: 'Semi-Annually',
    value: SalesTaxFilingFrequencyEnum.SEMI_ANNUALLY,
    minMonths: 6,
  },
  {
    label: 'Annual Calendar Year',
    value: SalesTaxFilingFrequencyEnum.ANNUAL_CALENDAR_YEAR,
    minMonths: 12,
  },
  {
    label: 'Fiscal Year',
    value: SalesTaxFilingFrequencyEnum.FISCAL_YEAR,
    minMonths: 12,
  },
];

export const FILING_FREQUENCY_LABEL_MAP: Partial<
  Record<SalesTaxFilingFrequencyEnum, string>
> = {
  [SalesTaxFilingFrequencyEnum.MONTHLY]: 'Monthly',
  [SalesTaxFilingFrequencyEnum.QUARTERLY]: 'Quarterly',
  [SalesTaxFilingFrequencyEnum.QUARTERLY_PREPAY_MONTHLY]:
    'Quarterly Prepay Monthly',
  [SalesTaxFilingFrequencyEnum.SEMI_ANNUALLY]: 'Semi-Annually',
  [SalesTaxFilingFrequencyEnum.ANNUAL_CALENDAR_YEAR]: 'Annual Calendar Year',
  [SalesTaxFilingFrequencyEnum.FISCAL_YEAR]: 'Fiscal Year',
  [SalesTaxFilingFrequencyEnum.MONTHLY_ACCELERATED_PREPAY_EARLY]:
    'Monthly Accelerated Prepay Early',
  [SalesTaxFilingFrequencyEnum.ANNUAL_RECONCILIATION]: 'Annual Reconciliation',
  [SalesTaxFilingFrequencyEnum.BI_MONTHLY]: 'Bi-Monthly',
  [SalesTaxFilingFrequencyEnum.TRI_ANNUALLY]: 'Tri-Annually',
  [SalesTaxFilingFrequencyEnum.QUARTERLY_FEB]: 'Quarterly (Feb start)',
  [SalesTaxFilingFrequencyEnum.QUARTERLY_MAR]: 'Quarterly (Mar start)',
};

// ============================================================================
// Calendar Card Variants
// ============================================================================

export const CALENDAR_CARD_VARIANTS = {
  new: {
    bgColor: 'bg-white',
    borderColor: 'border-utility-blue-300',
    borderLeftColor: 'border-l-utility-blue-500',
  },
  existing: {
    bgColor: 'bg-utility-warning-50',
    borderColor: 'border-utility-warning-200',
    borderLeftColor: 'border-l-utility-warning-500',
  },
  'to-delete': {
    bgColor: 'bg-bg-error-primary',
    borderColor: 'border-utility-error-200',
    borderLeftColor: 'border-l-utility-error-500',
  },
  skipped: {
    bgColor: 'bg-white',
    borderColor: 'border-utility-error-300',
    borderLeftColor: 'border-l-utility-error-500',
  },
} as const;

// ============================================================================
// Validation Messages
// ============================================================================

export const VALIDATION_MESSAGES = {
  STATE_REQUIRED: 'Please select a state',
  TAX_PERIOD_START_REQUIRED: 'Please select a tax period start month',
  FILING_FREQUENCY_REQUIRED: 'Please select a filing frequency',
  DATE_RANGE_TOO_SHORT: (frequency: string, minMonths: number) =>
    `Selected date range is too short for ${frequency} frequency. Minimum ${minMonths} month(s) required.`,
  END_BEFORE_START: 'Tax period end must be after tax period start',
  FILED_CONFLICT:
    'Cannot generate calendar. There are filed filings within the selected range.',
  COVERAGE_GAP:
    'This tax period is not included in any filing. Adjust the start or end month to confirm and continue.',
  OVERLAP_WITH_SCHEDULED:
    'Please change the tax period start month to proceed. There is an overlap with a scheduled filing.',
} as const;

// ============================================================================
// Month Names
// ============================================================================

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

// ============================================================================
// Alert Configuration (maps to Alert component variants)
// ============================================================================

export const ALERT_CONFIG = {
  coverageGap: {
    variant: 'warning' as const,
    title: 'Skipped filing(s)',
  },
  filedConflict: {
    variant: 'destructive' as const,
    title: 'Cannot generate calendar',
  },
  overlapWarning: {
    variant: 'warning' as const,
    title: 'Overlapping tax period',
  },
  validationError: {
    variant: 'destructive' as const,
    title: 'Validation error',
  },
  success: {
    variant: 'success' as const,
    title: 'Calendar generated successfully',
  },
} as const;
